import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Search as SearchIcon, X, Hash, Folder, ArrowRight } from 'lucide-react';
import api from '../config/api';
import VerifiedAvatar from './VerifiedAvatar';

const DEBOUNCE_MS = 200;

/**
 * Inline Search (no modal, no portal).
 * UX: dropdown appears under the input once query has at least 1 character.
 * It reuses the same API and navigation logic as SearchSpotlight.
 */
const SearchInline = ({
    className = '',
    inputClassName = '',
    inputTextClassName = '',
    iconClassName = '',
    placeholder = 'Поиск профилей, категорий, тегов…',
    autoFocus = false,
    minChars = 1,
    portal = true,
    portalZIndexClassName = 'z-[60]'
}) => {
    const navigate = useNavigate();
    const rootRef = useRef(null);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    const [anchorRect, setAnchorRect] = useState(null);
    const anchorRectRef = useRef(null);
    const rafRef = useRef(null);

    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [open, setOpen] = useState(false);
    const flatListRef = useRef([]);

    // Build a flat render list with section headers (same idea as SearchSpotlight)
    const buildFlat = useCallback((sug) => {
        const out = [];
        const pushSection = (title, items, type) => {
            if (!items || items.length === 0) return;
            out.push({ kind: 'header', title });
            items.forEach((item) => out.push({ kind: type, item }));
        };
        pushSection('Профили', sug.profiles, 'profile');
        pushSection('Категории', sug.categories, 'category');
        pushSection('Теги', (sug.tags || []).map((t) => ({ tag: t })), 'tag');
        return out;
    }, []);

    const queryTrimmed = useMemo(() => (query || '').trim(), [query]);
    const queryOk = queryTrimmed.length >= Number(minChars || 1);

    // Fetch suggestions (debounced)
    useEffect(() => {
        if (!queryOk) {
            setBusy(false);
            flatListRef.current = [];
            setActiveIndex(0);
            setOpen(false);
            return;
        }

        let cancelled = false;
        setOpen(true);
        const t = setTimeout(async () => {
            setBusy(true);
            try {
                const res = await api.get(`/api/search/suggest?q=${encodeURIComponent(queryTrimmed)}`);
                if (cancelled) return;
                const data = res?.data || { profiles: [], tags: [], categories: [] };
                flatListRef.current = buildFlat(data);
                const idx = flatListRef.current.findIndex((r) => r.kind !== 'header');
                setActiveIndex(idx >= 0 ? idx : 0);
            } catch (_) {
                // ignore
            } finally {
                if (!cancelled) setBusy(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(t);
            cancelled = true;
        };
    }, [queryOk, queryTrimmed, buildFlat]);

    // Auto focus if desired
    useEffect(() => {
        if (!autoFocus) return;
        const t = setTimeout(() => {
            try { inputRef.current?.focus(); } catch (_) { }
        }, 20);
        return () => clearTimeout(t);
    }, [autoFocus]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const onDocDown = (e) => {
            try {
                if (!rootRef.current) return;
                if (rootRef.current.contains(e.target)) return;
                if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
                setOpen(false);
            } catch (_) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocDown);
        document.addEventListener('touchstart', onDocDown, { passive: true });
        return () => {
            document.removeEventListener('mousedown', onDocDown);
            document.removeEventListener('touchstart', onDocDown);
        };
    }, [open]);

    // Measure anchor rect for portal dropdown positioning
    const measureAnchor = useCallback(() => {
        try {
            const el = rootRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const nextRect = {
                left: rect.left,
                top: rect.top,
                right: rect.right,
                bottom: rect.bottom,
                width: rect.width,
                height: rect.height
            };
            const prev = anchorRectRef.current;
            const unchanged = prev
                && prev.left === nextRect.left
                && prev.top === nextRect.top
                && prev.right === nextRect.right
                && prev.bottom === nextRect.bottom
                && prev.width === nextRect.width
                && prev.height === nextRect.height;
            if (unchanged) return;
            anchorRectRef.current = nextRect;
            setAnchorRect(nextRect);
        } catch (_) {
            // ignore
        }
    }, []);

    useEffect(() => {
        if (!open || !portal) return;
        measureAnchor();

        const scheduleMeasure = () => {
            if (rafRef.current) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                measureAnchor();
            });
        };
        const onScroll = () => scheduleMeasure();
        const onResize = () => scheduleMeasure();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [open, portal, measureAnchor]);

    const navigateRow = useCallback((row) => {
        if (!row || row.kind === 'header') return;

        if (row.kind === 'profile') {
            const p = row.item;
            if (p?.id) navigate(`/user/${encodeURIComponent(String(p.id))}`);
            setOpen(false);
            return;
        }

        if (row.kind === 'category') {
            const c = row.item;
            if (c?.slug) navigate(`/feed?category=${encodeURIComponent(String(c.slug))}`);
            else navigate('/feed');
            setOpen(false);
            return;
        }

        if (row.kind === 'tag') {
            navigate(`/tag/${encodeURIComponent(row.item.tag)}`);
            setOpen(false);
        }
    }, [navigate]);

    const goFullSearch = useCallback((type = 'all') => {
        if (!queryOk) return;
        navigate(`/search?q=${encodeURIComponent(queryTrimmed)}&type=${encodeURIComponent(type)}`);
        setOpen(false);
    }, [navigate, queryOk, queryTrimmed]);

    const handleKeyDown = (e) => {
        if (!open) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            return;
        }

        const list = flatListRef.current || [];
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (list.length === 0) return;
            let i = activeIndex;
            do {
                i = Math.min(list.length - 1, i + 1);
            } while (i < list.length && list[i]?.kind === 'header' && i < list.length - 1);
            setActiveIndex(i);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (list.length === 0) return;
            let i = activeIndex;
            do {
                i = Math.max(0, i - 1);
            } while (i > 0 && list[i]?.kind === 'header');
            setActiveIndex(i);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const row = (flatListRef.current || [])[activeIndex];
            if (!row) return;
            navigateRow(row);
        }
    };

    const clearQuery = () => {
        setQuery('');
        flatListRef.current = [];
        setActiveIndex(0);
        setOpen(false);
        try { inputRef.current?.focus(); } catch (_) { }
    };

    const renderList = flatListRef.current || [];

    const dropdown = (
        <div
            ref={dropdownRef}
            className={
                `rounded-2xl border border-white/30 bg-white/80 backdrop-blur-md ` +
                `shadow-2xl overflow-hidden ${portalZIndexClassName}`
            }
        >
            <div className="max-h-[52vh] overflow-auto p-2 sm:p-3">
                {renderList.length === 0 && !busy && (
                    <div className="text-center py-8 text-secondary-600 text-sm">Ничего не найдено</div>
                )}

                {renderList.map((row, idx) => {
                    if (row.kind === 'header') {
                        return (
                            <div key={`h-${row.title}-${idx}`} className="px-2 pt-3 pb-1 text-[11px] uppercase tracking-wider text-secondary-500">
                                {row.title}
                            </div>
                        );
                    }
                    const active = idx === activeIndex;

                    if (row.kind === 'profile') {
                        const u = row.item;
                        return (
                            <button
                                type="button"
                                key={`u-${u.id}`}
                                onMouseEnter={() => setActiveIndex(idx)}
                                onClick={() => navigateRow(row)}
                                className={`w-full flex items-center px-2 sm:px-3 py-2 rounded-xl transition-colors ${active ? 'bg-white' : 'hover:bg-white/70'}`}
                            >
                                <div className="mr-3 flex-shrink-0">
                                    <VerifiedAvatar
                                        user={u}
                                        userId={u?.id}
                                        src={u?.avatar_url}
                                        alt={u?.username}
                                        size={40}
                                        fallbackText={(u?.username || 'U').charAt(0)}
                                        badgePositionClass="bottom-0 right-0"
                                    />
                                </div>
                                <div className="flex-1 text-left">
                                    <div className="text-sm text-secondary-900 line-clamp-1">{u.title ? u.title : `@${u.username}`}</div>
                                    <div className="text-xs text-secondary-500 line-clamp-1">@{u.username}</div>
                                </div>
                                {active && <ArrowRight className="w-4 h-4 text-secondary-500" />}
                            </button>
                        );
                    }

                    if (row.kind === 'category') {
                        const c = row.item;
                        return (
                            <button
                                type="button"
                                key={`c-${c.id}`}
                                onMouseEnter={() => setActiveIndex(idx)}
                                onClick={() => navigateRow(row)}
                                className={`w-full flex items-center px-2 sm:px-3 py-2 rounded-xl transition-colors ${active ? 'bg-white' : 'hover:bg-white/70'}`}
                            >
                                <div className="w-10 h-10 rounded-lg bg-secondary-100 mr-3 flex items-center justify-center flex-shrink-0">
                                    <Folder className="w-5 h-5 text-secondary-400" />
                                </div>
                                <div className="flex-1 text-left">
                                    <div className="text-sm text-secondary-900 line-clamp-1">{c.name}</div>
                                    <div className="text-xs text-secondary-500 line-clamp-1">Категория</div>
                                </div>
                                {active && <ArrowRight className="w-4 h-4 text-secondary-500" />}
                            </button>
                        );
                    }

                    if (row.kind === 'tag') {
                        const t = row.item.tag;
                        return (
                            <button
                                type="button"
                                key={`t-${t}`}
                                onMouseEnter={() => setActiveIndex(idx)}
                                onClick={() => navigateRow(row)}
                                className={`w-full flex items-center px-2 sm:px-3 py-2 rounded-xl transition-colors ${active ? 'bg-white' : 'hover:bg-white/70'}`}
                            >
                                <div className="w-10 h-10 rounded-lg bg-secondary-100 mr-3 flex items-center justify-center flex-shrink-0">
                                    <Hash className="w-5 h-5 text-secondary-400" />
                                </div>
                                <div className="flex-1 text-left">
                                    <div className="text-sm text-secondary-900 line-clamp-1">#{t}</div>
                                    <div className="text-xs text-secondary-500">Тег</div>
                                </div>
                                {active && <ArrowRight className="w-4 h-4 text-secondary-500" />}
                            </button>
                        );
                    }
                    return null;
                })}
            </div>

            <div className="px-3 sm:px-4 py-2 border-t border-white/40 bg-white/70 flex items-center justify-end">
                <button
                    type="button"
                    onClick={() => goFullSearch('all')}
                    className="px-2 py-1 rounded-md bg-primary-500 text-white hover:bg-primary-600 text-xs"
                >
                    Полный поиск
                </button>
            </div>
        </div>
    );

    return (
        <div ref={rootRef} className={`relative ${className}`}>
            {/* Input */}
            <div
                className={
                    `flex items-center gap-2 px-4 py-3 rounded-2xl border border-white/40 ` +
                    `bg-white/70 backdrop-blur-md shadow-soft ` +
                    `focus-within:ring-2 focus-within:ring-primary-400/60 focus-within:border-primary-400/40 ` +
                    `${inputClassName}`
                }
            >
                <SearchIcon className={`w-5 h-5 text-secondary-700 ${iconClassName}`} />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => {
                        // only open when we have enough chars
                        if (queryOk) setOpen(true);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className={
                        `flex-1 bg-transparent focus:outline-none placeholder-secondary-400 ` +
                        `text-secondary-900 text-sm sm:text-base ${inputTextClassName}`
                    }
                    autoComplete="off"
                />

                {busy && queryOk && (
                    <span className="text-xs text-secondary-500">Поиск…</span>
                )}

                {!!query && (
                    <button
                        type="button"
                        onClick={clearQuery}
                        className="p-2 rounded-md hover:bg-white/60 transition-colors"
                        aria-label="Clear"
                        title="Очистить"
                    >
                        <X className="w-5 h-5 text-secondary-700" />
                    </button>
                )}
            </div>

            {/* Dropdown (only with 1+ chars) */}
            {open && queryOk && !portal && (
                <div className="absolute left-0 right-0 mt-2">
                    {dropdown}
                </div>
            )}

            {open && queryOk && portal && anchorRect && (
                createPortal(
                    <div
                        className="fixed z-10"
                        style={{
                            left: Math.max(8, Math.round(anchorRect.left)),
                            top: Math.round(anchorRect.bottom + 8),
                            width: Math.max(240, Math.round(anchorRect.width)),
                            maxWidth: Math.round(window.innerWidth - 16)
                        }}
                    >
                        {dropdown}
                    </div>,
                    document.body
                )
            )}
        </div>
    );
};

export default SearchInline;
